import React, { useEffect, useMemo, useReducer, useState } from "react";
import { createIntl, useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import { useAppConfig } from "@ct-react/core";
import { defaultLocale, LocaleLink } from "@ct-react/locale";
import { AccommodationType } from "@shared/models/article";
import { getIntlMessages } from "../../i18n";
import { DefaultContactFormData } from "../../models/forms.data";
import { buttonsTranslations, formTranslations, genericTermTranslations } from "../../i18n/sharable-defs";
import { footerTranslations } from "../../layout/footer/translations";
import { contactFormReducer } from "./forms.reducer";
import { EMAIL_MUTATION } from "./forms.config";
import { RedButton } from "../buttons/buttons";
import { Loader } from "../loaders/loaders";
import PopUp from "../pop-up/pop-up";
import Chevron_down from "../../../assets/svg/chevron-down-solid.svg";
import "./forms.scss";

const ContactFormInitialData = new DefaultContactFormData();

type FormEvent = React.FormEvent<HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement>;

type ContacFormProps = {
    articleId?: string | null,
    type?: AccommodationType,
    email?:string
};

export const ContactForm = ({
    articleId = undefined,
    type,
    email
}: ContacFormProps) => {

    const intl = useIntl();
    const fr = createIntl({ locale: defaultLocale.code, messages: getIntlMessages(defaultLocale) });

    const { options: { agencyId } } = useAppConfig();
    const [formData, dispatchFormData] = useReducer<(formData: DefaultContactFormData, action: any) => DefaultContactFormData>(contactFormReducer, ContactFormInitialData);
    const [isDisabled,setIsDisabled] = useState<boolean>(true);
    const [succeeded, setSucceeded] = useState<boolean | null>(null);

    const [ sendEmail, { loading: actionLoading } ] = useMutation(EMAIL_MUTATION);

    const handleFormData = (e: FormEvent) => {
        dispatchFormData({
            type: e.currentTarget.name,
            value: e.currentTarget.value,
        });
        if(Object.values(formData).includes(false)){
            setIsDisabled(true);
        }else{
            setIsDisabled(false);
        }
    };

    const handleRecipient = () => {
        switch (type?.toLowerCase() || formData.subject) {
            case ("sale"):
            case ("Acheter"):
            case ("Buy"):
            case ("Mettre en vente"):
            case ("Put on sale"):
                return "vente@montanagence.ch";

            case ("weekly"):
            case ("annual"):
            case ("seasonal"):
            case ("Louer"):
            case ("Rent"):
            case ("Mettre en location"):
            case ("Put on rent"):
            default:
                return "location@montanagence.ch";
        }
    };

    const handleSubject = () => {
      switch (type) {
        case AccommodationType.RENTAL:
          return fr.formatMessage(genericTermTranslations.locationHolidays);
        case AccommodationType.SEASONAL:
          return fr.formatMessage(genericTermTranslations.locationSeasonal);
        case AccommodationType.ANNUAL:
          return fr.formatMessage(genericTermTranslations.locationYearly);
        case AccommodationType.SALE:
          return fr.formatMessage(genericTermTranslations.sale);
      }
    };

    useEffect(() => {
        if(Object.values(formData).includes(false)){
            setIsDisabled(true)
        }else{
            setIsDisabled(false)
        }
    },[formData]);

    const formCallback = useMemo(() => {

        if(succeeded != null) {
            return(
                <PopUp succeeded={succeeded} setState={() => setSucceeded(null)}>
                    <h3>
                        {succeeded?
                        intl.formatMessage(formTranslations.sent)
                        :
                        intl.formatMessage(formTranslations.failed)
                        }
                    </h3>
                    <p>
                        {succeeded?
                        intl.formatMessage(formTranslations.sent_text)
                        :
                        intl.formatMessage(formTranslations.failed_text)
                        }
                    </p>
                    <div className="form-result-buttons">
                        <button className="btn-red inverted" onClick={() => setSucceeded(null)}>
                            {intl.formatMessage(buttonsTranslations.close)}
                        </button>
                        {succeeded ?
                        <LocaleLink className="btn-red" to={"/"}>
                            {intl.formatMessage(buttonsTranslations.back_home)}
                        </LocaleLink>
                        :
                        <LocaleLink className="btn-red" to={"contact"}>
                            {intl.formatMessage(buttonsTranslations.retry)}
                        </LocaleLink>
                        }
                    </div>
                </PopUp>
            );
        }
        return undefined;
    },[succeeded])

    const handleSubmit = () => {
        sendEmail({
            variables: {
              agencyId,
              firstName : formData.surname,
              lastName : formData.name,
              email : formData.email,
              phone : formData.tel,
              subject : (!!type || formData.subject === "") ? handleSubject() : formData.subject,
              body : formData.message,
              ...(!!articleId) && { article: { articleId } },
              emailAddress : !!email ? email : handleRecipient()
            }
        })
        .then(() => dispatchFormData({ type: "reset", }))
        .then(() => setSucceeded(true))
        .catch(() => setSucceeded(false));
    };

    return(
        <>
            {actionLoading&& <Loader/>}
            <form id="contact-form" className="contact-form">

                <div className="input-block surname">
                    <label htmlFor="surname">{`${intl.formatMessage(formTranslations.firstNameLabel)}*`}</label>
                    <input
                        id="surname"
                        type="text"
                        name="surname"
                        value={formData.surname}
                        placeholder={intl.formatMessage(formTranslations.firstNamePlaceholder)}
                        required
                        onInput={handleFormData}
                    />
                    <span>{formData.surname === "" || !formData.surnameValid && intl.formatMessage(formTranslations.emptyError)}</span>
                </div>

                <div className="input-block name">
                    <label htmlFor="name">{`${intl.formatMessage(formTranslations.lastNameLabel)}*`}</label>
                    <input
                        id="name"
                        type="text"
                        name="name"
                        value={formData.name}
                        placeholder={intl.formatMessage(formTranslations.lastNamePlaceholder)}
                        required
                        onInput={handleFormData}
                    />
                    <span>{formData.name === "" || !formData.nameValid && intl.formatMessage(formTranslations.emptyError)}</span>
                </div>

                <div className="input-block email">
                    <label htmlFor="email">{`${intl.formatMessage(formTranslations.emailLabel)}*`}</label>
                    <input
                        id="email"
                        type="email"
                        name="email"
                        value={formData.email}
                        placeholder={intl.formatMessage(formTranslations.emailPlaceholder)}
                        required
                        onInput={handleFormData}
                    />
                    <span>{formData.email === "" || !formData.emailValid && intl.formatMessage(formTranslations.mailError)}</span>
                </div>

                <div className="input-block tel">
                    <label htmlFor="tel">{`${intl.formatMessage(formTranslations.phoneLabel)}*`}</label>
                    <input
                        id="tel"
                        type="tel"
                        name="tel"
                        value={formData.tel}
                        placeholder={intl.formatMessage(formTranslations.phonePlaceholder)}
                        onInput={handleFormData}
                    />
                    <span>{formData.tel === "" || !formData.telValid && intl.formatMessage(formTranslations.phoneError)}</span>
                </div>

                {articleId === undefined &&
                <div className="input-block subject">
                    <label htmlFor="subject">{`${intl.formatMessage(formTranslations.subjectLabel)}*`}</label>
                    <select id="subject" name="subject" required onInput={handleFormData} defaultValue={""}>
                        <option value="" disabled>{intl.formatMessage(genericTermTranslations.choose_option)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.other)}>{intl.formatMessage(genericTermTranslations.other)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.buy)}>{intl.formatMessage(genericTermTranslations.buy)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.put_on_sale)}>{intl.formatMessage(genericTermTranslations.put_on_sale)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.location)}>{intl.formatMessage(genericTermTranslations.location)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.put_on_rent)}>{intl.formatMessage(genericTermTranslations.put_on_rent)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.promotion)}>{intl.formatMessage(genericTermTranslations.promotion)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.domiciliation)}>{intl.formatMessage(genericTermTranslations.domiciliation)}</option>
                        <option value={fr.formatMessage(genericTermTranslations.managment_cooproprety)}>{intl.formatMessage(genericTermTranslations.managment_cooproprety)}</option>
                    </select>
                    <Chevron_down/>
                </div>
                }

                <div className="input-block message">
                    <label htmlFor="message">{`${intl.formatMessage(formTranslations.messageLabel)}*`}</label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        placeholder={intl.formatMessage(formTranslations.messagePlaceholder)}
                        required
                        onInput={handleFormData}
                    >
                    </textarea>
                    <span>{formData.message === "" || !formData.messageValid && intl.formatMessage(formTranslations.emptyError)}</span>
                </div>

                <RedButton uppercase={true} type="button" disabled={isDisabled} onClick={handleSubmit}>
                    {intl.formatMessage(buttonsTranslations.send)}
                </RedButton>

                {!!articleId &&
                    <div className="legalMention">
                        <LocaleLink to={"mentions-legales"}>{intl.formatMessage(footerTranslations.mentions)}</LocaleLink>
                        <LocaleLink to={"politique-de-confidentialite"}>{intl.formatMessage(footerTranslations.policy)}</LocaleLink>
                    </div>
                }
            </form>
            {formCallback}
        </>
    )
};

export const NewsletterForm = () => {

  const intl = useIntl();
  const { options: { agencyId } } = useAppConfig();

  const [ firstName, setFirstName ] = useState<string | undefined>(undefined);
  const [ lastName, setLastName ] = useState<string | undefined>(undefined);
  const [ email, setEmail ] = useState<string | undefined>(undefined);
  const [ sendable, setSendable ] = useState<boolean>(false);
  const [ success, setSuccess ] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const newValue = !!firstName && !!lastName && /.+@.+\..+/.test(email || "");
    sendable !== newValue && setSendable(newValue);
  }, [ firstName, lastName, email ]);

  const [ sendMail ] = useMutation(EMAIL_MUTATION);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    sendMail({
      variables: {
        agencyId,
        firstName,
        lastName,
        email,
        phone: "inconnu",
        subject: "Souscription newsletter",
        body: "Demande de souscription à la newsletter"
      }
    })
    .then(() => setSuccess(true))
    .catch(() => setSuccess(false));
  }

  const resultRendering = useMemo(() => {
    if (success !== undefined)
      return (<PopUp succeeded={success} setState={() => setSuccess(undefined)}>
        <h3>{intl.formatMessage(success ? formTranslations.newsletterSent : formTranslations.failed)}</h3>
      </PopUp>);
    return null;
  }, [ success ]);

  return (
    <>
      <form>
        <div className="input-block name">
          <label htmlFor="firstname">{`${intl.formatMessage(formTranslations.firstNameLabel)}*`}</label>
          <input
            id="firstname"
            type="text"
            placeholder={intl.formatMessage(formTranslations.firstNamePlaceholder)}
            value={firstName || ""}
            onChange={e => setFirstName(e.target.value)}
            required />
          <label htmlFor="lastname">{`${intl.formatMessage(formTranslations.lastNameLabel)}*`}</label>
          <input
            id="lastname"
            type="text"
            placeholder={intl.formatMessage(formTranslations.lastNamePlaceholder)}
            value={lastName || ""}
            onChange={e => setLastName(e.target.value)}
            required />
        </div>

        <div className="input-block email">
          <label htmlFor="email">{`${intl.formatMessage(formTranslations.emailLabel)}*`}</label>
          <input
            id="email"
            type="email"
            placeholder={intl.formatMessage(formTranslations.emailPlaceholder)}
            value={email || ""}
            onChange={e => setEmail(e.target.value)}
            required />
        </div>

        <RedButton
          uppercase={false}
          type="button"
          disabled={!sendable}
          onClick={handleSubmit}>
          {intl.formatMessage(buttonsTranslations.subscribe)}
        </RedButton>

      </form>
      {resultRendering}
    </>);

};

